import axiosInstance from "Utils/axiosInstance";
import { base_url } from "Zustand/constants";
import axios from "axios";
import { toast } from "react-toastify";

export interface UploadFileInput {
  nodeid: string;
  filename: string;
}

export interface UploadFileInputByFileId {
  nodeid: string;
  fileid: string;
}

type TestPlanResponse = {
  testplan: {
    nodeid: string;
    hasattachment?: ApiAttachment[];
  };
};

type TestReportResponse = {
  testreport: {
    nodeid: string;
    hasattachment?: ApiAttachment[];
    options?: TestReportGenerateOption[];
    testreportoptions?: TestReportGenerateOption[];
    testreporttemplatenodeid?: string;
    acceptancecriteria?: string;
  };
};

type ApiAttachment = {
  attachment: {
    title: string;
    description: string;
    specifier?: string;
    file: {
      fileid: string;
      fileversion: string;
      url: string;
      mimetype?: string;
    };
    nodeid: string; // attachment nodeid
  };
};

export interface UploadFileResponse {
  fileid: string;
  bucketid: string;
  url: string;
  fileversion: string;
  mimetype: string;
}

export type TestReportGenerateOption = {
  identifier: string;
  label?: string;
  value: boolean;
};

export type GenerateTestReportPayload = {
  options?: TestReportGenerateOption[];
  testreporttemplatenodeid?: string;
  splitgraphs?: boolean;
};

export type GenerateTestPlanPayload = {
  testplantemplatenodeid?: string;
  username?: string;
};

export const uploadFilesAPI = async (
  file: UploadFileInput[],
): Promise<UploadFileResponse[]> => {
  const res = await axiosInstance.post(`${base_url}/files`, {
    filenames: file,
  });

  if (res.status !== 200) {
    throw new Error("Failed to obtain upload links");
  }

  return res.data;
};

export const uploadFilesAPIByFileId = async (
  file: UploadFileInputByFileId[],
): Promise<UploadFileResponse[]> => {
  const res = await axiosInstance.post(`${base_url}/files`, {
    fileids: file,
  });

  if (res.status !== 200) {
    throw new Error("Failed to obtain upload links");
  }

  return res.data;
};

export const createAttachmentAPI = async (
  attachmentsPayload: {
    lang: string;
    title: string;
    description: string;
    // specifier: string;
    file: {
      fileid: string;
      bucketid: string;
      fileversion: string;
      mimetype: string;
    };
    isattachmentofnodeid: string;
  }[],
) => {
  const res = await axiosInstance.post("/attachments", attachmentsPayload);
  if (res.status !== 200 && res.status !== 201) {
    throw new Error("Failed to create attachments");
  }
  return res.data;
};

export const updateAttachmentAPI = async (data: {
  nodeid: string;
  lang: string;
  title: string;
  description: string;
}) => {
  const response = await axiosInstance.put(`/attachments`, data);
  if (response.status !== 200) throw new Error("Failed to update attachment");
  return response.data;
};

export const uploadFilesWithProgress = async (
  uploadMetaList: any[],
  originalAttachment: { file: File }[],
) => {
  const uploadPromises = uploadMetaList?.map((meta, index) => {
    const file = originalAttachment[index]?.file;
    if (!file) return Promise.resolve();

    // Create toast ID so we can update it later
    const toastId = toast(`Uploading ${file.name}...`, {
      progress: 0,
      autoClose: false,
    });

    return axios
      .put(meta.url, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );

          toast.update(toastId, {
            render: `${file.name} (${percent}%)`,
            progress: percent / 100,
          });
        },
      })
      .then(() => {
        toast.update(toastId, {
          render: `${file.name} uploaded successfully`,
          type: "success",
          progress: 1,
        });
      })
      .catch((err) => {
        toast.update(toastId, {
          render: `Upload failed: ${file.name}`,
          type: "error",
          autoClose: 5000,
          isLoading: false,
        });
      });
  });

  await Promise.all(uploadPromises);
};

export const deleteAttachmentsAPI = async (nodeIds: string[]) => {
  const results = await Promise.allSettled(
    nodeIds.map((nodeid) =>
      axiosInstance.delete("/attachments", {
        data: { nodeid },
      }),
    ),
  );

  const failed = results.filter((res) => res.status === "rejected");
  if (failed.length) {
    throw new Error(`Failed to delete ${failed.length} attachments`);
  }

  return results;
};

export async function getAttachmentByNodeId(nodeid: string) {
  // GET /api/v1/attachments/{attachmentnodeid}
  const { data } = await axiosInstance.get(
    `/attachments/${encodeURIComponent(nodeid)}`,
  );
  // typical shape: { attachment: { file: { url: string, ... }, ... } }
  const url =
    data?.attachment?.file?.url ??
    data?.file?.url ?? // fallback if backend returns a flattened shape
    null;

  if (!url) throw new Error("Attachment URL not found for node " + nodeid);
  return url as string;
}

export async function postGenerateTestPlan(
  nodeid: string,
  payload?: GenerateTestPlanPayload
) {
  const encoded = encodeURIComponent(nodeid);
  // POST /api/v1/testplans/{nodeid}/generate
  const { data } = await axiosInstance.post(
    `/testplans/${encoded}/generate`,
    payload ?? {}
  );
  return data;
}

export async function postGenerateTestReport(
  nodeid: string,
  payload?: GenerateTestReportPayload
) {
  const encoded = encodeURIComponent(nodeid);
  // POST /api/v1/testreports/{nodeid}/generate
  const { data } = await axiosInstance.post(
    `/testreports/${encoded}/generate`,
    payload ?? {}
  );
  return data;
}

export async function fetchTestPlan(nodeid: string): Promise<TestPlanResponse> {
  const encoded = encodeURIComponent(nodeid);
  const { data } = await axiosInstance.get(`/testplans/${encoded}`);
  return data;
}

export async function fetchTestReports(
  nodeid: string,
): Promise<TestReportResponse> {
  const encoded = encodeURIComponent(nodeid);
  const { data } = await axiosInstance.get(`/testreports/${encoded}`);
  return data;
}
